<script>
  // Scale a page from a drawn dimension: the user picks the two ends of a
  // written dimension with the pick-scale-dim tool, types the value as
  // written, sees the computed scale, and confirms before anything is
  // applied.
  //
  // A floating, non-blocking card in the canvas corner (the DocRulerWidget
  // pattern), NOT a modal: the user zooms far in to click the dimension ends
  // precisely, then has to zoom back out to read the written value, so the
  // canvas must stay interactive while this is open. Completing another
  // two-point pick while open re-measures in place and keeps the typed value.
  import { tick } from "svelte";
  import { DimText } from "dimtext";

  import XIcon from "@local/assets/icons/x.svg";
  import { getScaleLabel } from "@local/extensions/utilities/scales.js";
  import { paperLength, scaleFromDimension } from "#src/extensions/scale-from-dimension.js";
  import { readDimensionSuggestion } from "#src/extensions/dimension-suggestion.js";
  import pageScaleUpdates from "#src/extensions/page-scale-updates.js";
  import { setPageFlags } from "#src/lib/framex/page-flags.js";
  import { api } from "#src/api/";
  import { UX_MODE } from "#src/env.js";
  import { currentTool } from "#src/stores/ui.js";
  import { alerts } from "#src/stores/alerts.js";

  export let group;
  export let doc;
  export let indexedLocations;

  // The old tool snapped silently within 2.5%; the same band is where a
  // standard scale is even worth offering. The 1% preselection band lives in
  // scale-from-dimension.js.
  const NEAREST_DISPLAY_THRESHOLD = 0.025;

  let visible = false;
  // Instructions-only state: the panel pops as soon as the tool is armed,
  // before any points exist. open() ends it.
  let waiting = false;
  let input;

  let pageId = null;
  let paperInches = 0;
  let valueText = "";
  let touched = false;
  let prefilled = false;
  let choice = null;
  let overrideExisting = true;
  let openToken = 0;

  $: unit = $group?.data?.settings?.display_unit ?? "inches";
  // Frames on the picked page whose clip already carries its own scale — the
  // population the override checkbox acts on, so the count and the write can't
  // disagree (same filter as pageScaleUpdates).
  $: overridableCount = (indexedLocations[pageId] || []).filter((l) => l.clip?.drawing_scale).length;
  $: dt = new DimText({ defaultUnit: unit });

  $: realInches = parseLength(valueText);
  $: result = realInches ? scaleFromDimension({ paperInches, realInches }) : null;
  $: showNearest = result && result.nearest.deviation <= NEAREST_DISPLAY_THRESHOLD;
  // The user's explicit pick wins while it stays offerable; otherwise follow
  // the computed default (standard scale within 1%, the measurement beyond).
  $: effective = choice === "nearest" && !showNearest ? "exact" : (choice ?? result?.suggested);

  // The tool leaving pick-scale-dim is the one closing signal: Escape, a
  // toolbar switch, and Apply all route through it, and Document.svelte's
  // checkCurrentTool clears the in-progress pick on the same change — so no
  // stale segment can outlive the panel. "pan" is exempt: right/middle-drag
  // panning flips $currentTool to "pan" only for the drag (handleRightDown,
  // restored from tempTool on release), and must not cancel the panel.
  $: if (visible && $currentTool !== "pick-scale-dim" && $currentTool !== "pan") {
    visible = false;
  }

  // Arming the tool pops the panel immediately, in an instructions-only state,
  // so the user is told what the two clicks are for before making them. Guarded
  // on !visible: re-arming (or a re-pick) while results are showing must leave
  // the current values up until the new second click re-measures. The reset
  // mirrors open()'s fresh path, and pageId = null in particular means the
  // open() that follows can never read as a same-page re-pick.
  $: if ($currentTool === "pick-scale-dim" && !visible) {
    pageId = null;
    paperInches = 0;
    valueText = "";
    touched = false;
    prefilled = false;
    choice = null;
    overrideExisting = true;
    // Invalidate any suggestion fetch still in flight from a cancelled
    // session, so it can't write into this fresh one.
    openToken++;
    waiting = true;
    visible = true;
  }

  function parseLength(text) {
    if (!text?.trim()) return null;
    const dim = dt.parse(text.trim());
    if (!dim?.value) return null;
    const n = dim.value.toNumber("inches");
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  // Called by Document.svelte on every completed two-point pick. First call
  // opens the panel; while it is open, another pick on the SAME page
  // re-measures in place and preserves whatever the user has typed. A pick on
  // a different page starts fresh — a value typed for one page's dimension
  // must not be carried onto another page.
  export const open = async ({ pageId: pid, pts, sheet }) => {
    // waiting's pageId is null, so an open() out of the waiting state is
    // always a fresh pick, never a same-page re-pick.
    const repick = visible && !waiting && pid === pageId;
    waiting = false;
    pageId = pid;
    paperInches = paperLength(pts);

    if (!repick) {
      valueText = "";
      touched = false;
      prefilled = false;
      choice = null;
      // Checked by default; unchecking is a per-apply choice, never sticky.
      overrideExisting = true;
      visible = true;
    }

    const token = ++openToken;
    await tick();
    input?.focus();

    // The PDF text layer near the picks, as an editable prefill only — never
    // over a value the user has typed. A scanned sheet simply finds nothing
    // and the field stays empty.
    if (sheet && !touched) {
      const suggestion = await readDimensionSuggestion({
        doc,
        page: { id: pid },
        sheet,
        pts,
        parse: parseLength,
        rpc: (...args) => api.rpc(...args),
      });
      if (suggestion && token === openToken && visible && !touched) {
        valueText = suggestion.text;
        prefilled = true;
      }
    }
  };

  function cancel() {
    visible = false;
    // Disarm; Document.svelte's checkCurrentTool clears the picked segment.
    $currentTool = "select";
  }

  function apply() {
    if (!result) return;
    const value = effective === "nearest" ? result.nearest.value : result.exact.value;

    const updates = pageScaleUpdates({
      doc,
      grp: $group,
      indexedLocations,
      pageIds: [pageId],
      value,
      overrideExisting,
    });
    group.update(updates);

    if (UX_MODE === "shpx") {
      // A real scale was just set, so the page can't be "not a scaled
      // drawing" — same unconditional clear the other scale writers make.
      setPageFlags(group, doc, [pageId], { notApplicable: false });
    }

    alerts.add({ type: "ok", message: `Set page scale to ${getScaleLabel(value)}`, timeout: 5000 });
    visible = false;
    $currentTool = "select";
  }

  function handleKeydown(e) {
    if (e.key === "Escape") {
      e.stopPropagation();
      cancel();
    } else if (e.key === "Enter" && result) {
      apply();
    }
  }

  function fmtDeviation(deviation) {
    return `${(deviation * 100).toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;
  }
</script>

{#if visible}
  <!-- Positioning belongs to DocCornerPanels (the shared top-right stack);
       this is just the card. When Measure Drawing is open it holds the top
       spot and this card stacks below it. -->
  <div class="w-72 pointer-events-auto text-xs">
    <div class="bg-white rounded border shadow-lg w-full">
      <div class="p-2 flex items-center border-b">
        <div class="grow">Set Scale from Dimension</div>
        <button on:click={cancel} class="rounded p-1 hover:bg-gray-200">
          <XIcon />
        </button>
      </div>
      <div class="p-2 space-y-3">
        {#if waiting}
          <p class="text-gray-500">Click both ends of a dimension on the drawing.</p>
          <div class="flex justify-end gap-2 items-center">
            <button class="btn-text" on:click={cancel}>Cancel</button>
          </div>
        {:else}
          <p class="text-gray-500">
            Type the dimension exactly as it is written between the two points you picked.
          </p>
          <div class="space-y-1">
            <input
              bind:this={input}
              bind:value={valueText}
              on:input={() => {
                touched = true;
                prefilled = false;
              }}
              on:keydown={handleKeydown}
              class="form-input w-full"
              placeholder={`e.g. 3' 6"`} />
            {#if prefilled}
              <div class="text-gray-500">Read from the drawing — check it matches before applying.</div>
            {:else if valueText.trim() && !realInches}
              <div class="text-red-500">Can't read this as a length.</div>
            {/if}
          </div>
          {#if result}
            <div class="space-y-1">
              <label class="flex gap-2 items-center">
                <input type="radio" checked={effective === "exact"} on:change={() => (choice = "exact")} />
                <span>
                  Measured scale: <span class="font-bold">{result.exact.label}</span>
                </span>
              </label>
              {#if showNearest}
                <label class="flex gap-2 items-center">
                  <input
                    type="radio"
                    checked={effective === "nearest"}
                    on:change={() => (choice = "nearest")} />
                  <span>
                    Standard scale: <span class="font-bold">{result.nearest.label}</span>
                    <span class="text-gray-500">({fmtDeviation(result.nearest.deviation)} off)</span>
                  </span>
                </label>
              {/if}
              <div class="text-gray-500">
                Measured {paperInches.toLocaleString("en-US", { maximumFractionDigits: 2 })}&Prime; on paper.
              </div>
            </div>
          {/if}
          {#if overridableCount > 0}
            <label class="flex gap-2 items-center">
              <input type="checkbox" bind:checked={overrideExisting} />
              <span>Apply scale to existing frames</span>
            </label>
          {/if}
          <div class="flex justify-end gap-2 items-center">
            <button class="btn-text" on:click={cancel}>Cancel</button>
            <button class="btn btn-primary" disabled={!result} on:click={apply}>Set Scale</button>
          </div>
        {/if}
      </div>
    </div>
  </div>
{/if}
